import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Container, Grid, Button } from '@mui/material';

// Helper for fade/slide animations
const useFadeInOnScroll = (threshold = 0.2) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const AboutPage = () => {
  // Hero fade-in
  const [heroRef, heroVisible] = useFadeInOnScroll(0.1);
  // Story+Mission fade-in
  const [storyRef, storyVisible] = useFadeInOnScroll(0.15);
  // CTA fade-in
  const [ctaRef, ctaVisible] = useFadeInOnScroll(0.15);

  return (
    <>
      {/* 1. Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: '50vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/aboutus_banner/aboutus_banner (1).jpg"
          alt="About Hero Background"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 1,
            filter: 'brightness(0.6)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.45)',
            zIndex: 2,
          }}
        />
        <Container
          sx={{
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            color: '#fff',
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'none' : 'translateY(40px)',
            transition: 'opacity 1.2s, transform 1.2s',
            px: { xs: 1, md: 0 },
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: '0.08em', textTransform: 'uppercase', textShadow: '0 4px 24px rgba(0,0,0,0.7)', fontSize: { xs: '2rem', md: '2.8rem' } }}>
            About DigiWood FZCO
          </Typography>
          <Typography variant="h5" sx={{ color: '#f5f3e7', fontWeight: 500, mb: 1, textShadow: '0 2px 8px rgba(0,0,0,0.7)', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
            Sustainable Timber Trade, Simplified.
          </Typography>
        </Container>
      </Box>

      {/* 2. Company Story + Mission Section (merged) */}
      <Box
        ref={storyRef}
        sx={{
          width: '100%',
          position: 'relative',
          aspectRatio: { xs: '1 / 1.2', sm: '16 / 9' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Full background image, no overlay */}
        <Box
          component="img"
          src="/aboutus_banner/aboutus_banner (2).jpg"
          alt="Who We Are + Mission Background"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 1,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={{ xs: 2, md: 6 }} alignItems="center" justifyContent="center" direction={{ xs: 'column', md: 'row' }}>
            {/* Left: Empty for image focus */}
            <Grid item xs={12} md={6} />
            {/* Right: Text block for both story and mission */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.92)',
                  borderRadius: 4,
                  p: { xs: 2, md: 5 },
                  boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)',
                  opacity: storyVisible ? 1 : 0,
                  transform: storyVisible ? 'none' : 'translateX(40px)',
                  transition: 'opacity 1s, transform 1s',
                  mx: { xs: 1, md: 0 },
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, color: 'primary.main', letterSpacing: '0.06em', fontSize: { xs: '1.3rem', md: '2rem' } }}>
                  Who We Are
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.15rem' }, mb: 3 }}>
                  DigiWood FZCO is a Dubai-based, FSC-certified timber trading company dedicated to sustainable sourcing and global export. Since our founding in Dec-2021, we have connected Gabon’s finest veneer and plywood with partners worldwide, using a stockless, analytics-driven model. Our team is passionate about responsible forestry, efficiency, and building trust in every shipment.
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, color: 'primary.main', letterSpacing: '0.06em', fontSize: { xs: '1.3rem', md: '2rem' } }}>
                  Our Mission
                </Typography>
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700, mb: 1, fontSize: { xs: '1.1rem', md: '2rem' } }}>
                  To deliver sustainable, FSC-certified timber from Gabon to the world—efficiently, ethically, and transparently.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: { xs: '0.98rem', md: '1.1rem' }, mt: 2 }}>
                  “We don’t just trade wood, we deliver trust.”
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 3. Final CTA */}
      <Box
        ref={ctaRef}
        sx={{
          width: '100%',
          position: 'relative',
          minHeight: { xs: 160, md: 220 },
          bgcolor: '#181818',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 10 },
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/aboutus_banner/aboutus_banner (3).jpg"
          alt="CTA Background"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 1,
            filter: 'brightness(0.4)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.55)',
            zIndex: 2,
          }}
        />
        <Container
          sx={{
            position: 'relative',
            zIndex: 3,
            textAlign: 'center',
            color: '#fff',
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? 'none' : 'translateY(40px)',
            transition: 'opacity 1.2s, transform 1.2s',
            px: { xs: 1, md: 0 },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, letterSpacing: '0.06em', color: '#fff', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
            Want to work with a trusted, FSC-certified timber partner?
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            href="/contact"
            sx={{ px: { xs: 3, md: 5 }, py: { xs: 1, md: 1.5 }, fontWeight: 700, borderRadius: 2, fontSize: { xs: '1rem', md: '1.18rem' }, boxShadow: '0 4px 16px 0 rgba(166,124,82,0.10)' }}
          >
            Contact Us
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default AboutPage; 